import { invalidateLocationQueries } from '@/lib/locations/invalidate';
import { toast } from 'sonner';
import { createMutationHook } from '@/hooks/lib/createMutationHook';
import { apiPatch, operationsClient } from '@/lib/api';
import { jobEndpoints } from '@/lib/api/endpoints';
import { normalizeJob } from '@/lib/normalizeJob';
import { invoiceKeys, jobKeys, schedulerKeys } from '@/lib/queryKeys';
import { markAutoInvoiceRequested } from '@/lib/jobs/autoInvoiceRequests';
import type { Job, UpdateJobDto } from '@/types/job';

/**
 * Updates an existing job via PATCH on the operations microservice.
 */
export const useUpdateJob = createMutationHook<Job, UpdateJobDto & { id: string }>({
  mutationFn: async ({ id, ...dto }) =>
    normalizeJob(
      await apiPatch<Record<string, unknown>, UpdateJobDto>(
        operationsClient,
        jobEndpoints.update(id),
        dto,
      ),
    ),
  onSuccess: (data, _variables, queryClient) => {
    // A typed service address is saved to the customer as a location on the
    // server, so this save may have created one. See `invalidateLocationQueries`.
    invalidateLocationQueries(queryClient, data.contactId);
    // Mirror the backend's own condition: it publishes JOB_AUTO_INVOICE_REQUESTED
    // only on the false -> true flip, and only for a job already in the
    // completed phase. Read the cached job BEFORE the invalidations below, so
    // this compares against the pre-save value. No cached job means we cannot
    // prove a flip, and claiming one would make the billing tab promise an
    // invoice nobody asked for.
    const previous = queryClient.getQueryData<Job>(jobKeys.detail(data.id));
    if (
      previous &&
      !previous.autoInvoiceOnComplete &&
      data.autoInvoiceOnComplete &&
      String(data.statusPhase ?? data.status).toUpperCase() === 'COMPLETED'
    ) {
      markAutoInvoiceRequested(data.id);
    }
    void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
    void queryClient.invalidateQueries({ queryKey: jobKeys.detail(data.id) });
    // Dispatch and Calendar receive job tags through scheduler booking lists.
    // Refresh them after any job edit so adding/removing a tag is reflected
    // immediately instead of waiting for a later scheduler refetch.
    void queryClient.invalidateQueries({ queryKey: schedulerKeys.bookingLists() });
    // A job save can raise an invoice — switching on auto-invoice for a job
    // that already completed does exactly that. The job's invoice list lives
    // under its own key, so without this the billing tab kept showing
    // "Not invoiced yet" over an invoice that existed.
    void queryClient.invalidateQueries({ queryKey: invoiceKeys.byJobPrefix(data.id) });
    toast.success('Job updated');
  },
});
